import React, { useState, useEffect } from 'react';
import { ThumbsUp, ThumbsDown, X, Send, CheckCircle } from 'lucide-react';

const NEGATIVE_CATEGORIES = [
  { id: 'incorrect', label: 'Incorrect information' },
  { id: 'didnt_answer', label: "Didn't answer my question" },
  { id: 'too_slow', label: 'Too slow' },
  { id: 'confusing', label: 'Confusing response' },
  { id: 'other', label: 'Other' }
];

const POSITIVE_CATEGORIES = [
  { id: 'accurate', label: 'Accurate information' },
  { id: 'helpful', label: 'Very helpful' },
  { id: 'fast', label: 'Quick response' },
  { id: 'clear', label: 'Clear and easy to understand' },
  { id: 'other', label: 'Other' }
];

export default function FeedbackButtons({ chatLogId, onFeedbackSubmit }) {
  const [showButtons, setShowButtons] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(null); // 'positive' or 'negative'
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Show buttons after a short delay
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowButtons(true);
    }, 2000); // 2 second delay

    return () => clearTimeout(timer);
  }, []);

  const handleThumbsUp = () => {
    setModalType('positive');
    setShowModal(true);
  };

  const handleThumbsDown = () => {
    setModalType('negative');
    setShowModal(true);
  };

  const submitFeedback = async (rating, category, text) => {
    if (!chatLogId) {
      console.error('No chatLogId provided for feedback');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          chatLogId,
          rating,
          category,
          text
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      setFeedbackGiven(true);
      setShowModal(false);
      
      if (onFeedbackSubmit) {
        onFeedbackSubmit({ rating, category, text });
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      // Still show as submitted to avoid frustrating the user
      setFeedbackGiven(true);
      setShowModal(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleModalSubmit = () => {
    submitFeedback(
      modalType,
      selectedCategory,
      feedbackText.trim() || null
    );
  };

  const handleModalClose = () => {
    // If they opened the modal but want to close without detailed feedback,
    // still record the basic rating
    submitFeedback(modalType, null, null);
  };

  const categories = modalType === 'positive' ? POSITIVE_CATEGORIES : NEGATIVE_CATEGORIES;

  // Don't render anything until delay has passed
  if (!showButtons) {
    return null;
  }

  // Show thank you message after feedback - MORE VISIBLE VERSION
  if (feedbackGiven) {
    return (
      <div className="mt-4 pt-3 border-t border-gray-200">
        <div className="flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2.5 rounded-lg text-sm font-medium">
          <CheckCircle className="w-4 h-4" />
          <span>Thanks for your feedback!</span>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Feedback Buttons - MORE PROMINENT VERSION */}
      <div className="mt-4 pt-3 border-t border-gray-200">
        <div className="flex items-center gap-3 bg-purple-50 px-4 py-2.5 rounded-lg">
          <span className="text-sm font-medium text-purple-900">Was this helpful?</span>
          <div className="flex gap-1">
            <button
              onClick={handleThumbsUp}
              className="p-2 rounded-full hover:bg-purple-200 transition-all group"
              title="Yes, this was helpful"
            >
              <ThumbsUp className="w-5 h-5 text-purple-600 group-hover:text-purple-800 group-hover:scale-110 transition-transform" />
            </button>
            <button
              onClick={handleThumbsDown}
              className="p-2 rounded-full hover:bg-purple-200 transition-all group"
              title="No, this wasn't helpful"
            >
              <ThumbsDown className="w-5 h-5 text-purple-600 group-hover:text-purple-800 group-hover:scale-110 transition-transform" />
            </button>
          </div>
        </div>
      </div>

      {/* Feedback Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
            style={{
              animation: 'slideUp 0.3s ease-out'
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {modalType === 'positive' ? '🎉 Glad it helped!' : '😕 Sorry about that'}
              </h3>
              <button
                onClick={handleModalClose}
                className="p-1 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Question */}
            <p className="text-sm text-gray-600 mb-4">
              {modalType === 'positive' 
                ? 'What did you like? (optional)' 
                : 'What went wrong? (optional)'}
            </p>

            {/* Category Options */}
            <div className="flex flex-wrap gap-2 mb-4">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(
                    selectedCategory === cat.id ? null : cat.id
                  )}
                  className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                    selectedCategory === cat.id
                      ? modalType === 'positive'
                        ? 'bg-green-100 border-green-300 text-green-700'
                        : 'bg-red-100 border-red-300 text-red-700'
                      : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            {/* Text Input */}
            <textarea
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              placeholder={
                modalType === 'positive'
                  ? 'Tell us more about what worked well...'
                  : 'Tell us how we can do better...'
              }
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              rows={3}
            />

            {/* Submit Button */}
            <div className="flex gap-3 mt-4">
              <button
                onClick={handleModalClose}
                className="flex-1 px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                Skip
              </button>
              <button
                onClick={handleModalSubmit}
                disabled={submitting}
                className={`flex-1 px-4 py-2 text-sm text-white rounded-lg flex items-center justify-center gap-2 transition-colors ${
                  modalType === 'positive'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-purple-600 hover:bg-purple-700'
                } disabled:opacity-50`}
              >
                {submitting ? (
                  'Sending...'
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Submit
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global keyframe styles */}
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(20px);
          }
          to { 
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
      `}} />
    </>
  );
}